import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { arDigits, fmtLocaleNum, fmtAED, fmtIsoWeekLabel, fmtIsoDate } from "@/lib/utils";
import FAQ from "@/components/faq/FAQ";
import WeeklyInterpretation from "@/components/insights/WeeklyInterpretation";
import { LatestWeekCard, Methodology } from "@/components/insights/InsightsSections";
import MonthlyBriefing from "@/components/insights/MonthlyBriefing";
import { weekValueBn } from "@/lib/weekValue";

/** /insights, hub landing page. Data-driven weekly recap listing.
 *  Sole source: Dubai Land Department Open Data Gateway, refreshed every Monday 11:00 UAE local time.
 *  Arabic mode uses Eastern Arabic-Indic numerals (٠-٩) + "د.إ" for AED so
 *  visitors who don't read Latin numbers still get every figure. */
export default function Insights() {
  const { lang, lh } = useLang();
  const isAR = lang === "ar";
  const { format, formatBn } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/insights/hub")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const summary = data?.summary;
  const weeks = data?.latest_weeks || [];
  const source = data?.source;

  return (
    <div className="min-h-screen bg-propertic-paper text-propertic-navy" dir={isAR ? "rtl" : "ltr"}>
      <Helmet>
        <title>{isAR ? "تحليلات سوق دبي العقاري | تحليل DLD والبيانات الرسمية" : "Dubai Real Estate Insights | DLD Market Analysis & Data"}</title>
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="description" content={isAR
          ? "تحليلات أسبوعية لسوق دبي العقاري تعتمد على البيانات المفتوحة لدائرة الأراضي، وتغطّي المبيعات، الأسعار، الصفقات، نشاط قيد الإنشاء، واتجاهات السوق."
          : "Weekly Dubai real estate insights based on DLD open data, covering sales, prices, transactions, off-plan activity and market trends."} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={isAR ? "تحليلات سوق دبي العقاري | تحليل DLD والبيانات الرسمية" : "Dubai Real Estate Insights | DLD Market Analysis & Data"} />
        <meta property="og:description" content={isAR
          ? "تحليلات أسبوعية لسوق دبي العقاري تعتمد على البيانات المفتوحة لدائرة الأراضي، وتغطّي المبيعات، الأسعار، الصفقات، نشاط قيد الإنشاء، واتجاهات السوق."
          : "Weekly Dubai real estate insights based on DLD open data, covering sales, prices, transactions, off-plan activity and market trends."} />
        <meta property="og:url" content={`https://thepropertic.ae/insights${isAR ? "?lang=ar" : ""}`} />
        <link rel="canonical" href={`https://thepropertic.ae/insights${isAR ? "?lang=ar" : ""}`} />
        <link rel="alternate" hrefLang="en" href="https://thepropertic.ae/insights" />
        <link rel="alternate" hrefLang="ar" href="https://thepropertic.ae/insights?lang=ar" />
        <link rel="alternate" hrefLang="x-default" href="https://thepropertic.ae/insights" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": isAR ? "الرئيسية" : "Home", "item": "https://thepropertic.ae/" },
            { "@type": "ListItem", "position": 2, "name": isAR ? "التحليلات الأسبوعية" : "Weekly Insights", "item": "https://thepropertic.ae/insights" }
          ]
        })}</script>
      </Helmet>

      <div className="pt-20 sm:pt-28 pb-12 sm:pb-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-3" data-testid="insights-eyebrow">
            {isAR ? "المصدر · دائرة الأراضي والأملاك" : "Source · Dubai Land Department"}
          </p>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4" data-testid="insights-title">
            {isAR ? (
              <>تحليلات سوق دبي العقاري، <span className="italic-serif text-propertic-gold">بلغة واضحة.</span></>
            ) : (
              <>Dubai real estate market insights, <span className="italic-serif text-propertic-gold">in plain English.</span></>
            )}
          </h1>
          <p className="text-[15px] sm:text-lg text-propertic-ink-2/90 leading-relaxed" data-testid="insights-intro">
            {isAR
              ? "قراءتكم الأسبوعية لنشاط دائرة الأراضي والأملاك الرسمي: الخلاصات الواضحة أولاً، ثم الأرقام الكاملة والمنهجية والتقارير السابقة في الأسفل."
              : "Your weekly reading of official Dubai Land Department activity: clear takeaways first, with the full figures, methodology and historical reports below."}
          </p>
          <p className="mt-4 text-[11px] font-mono-brand text-propertic-muted tracking-[0.08em] leading-relaxed" data-testid="insights-dld-attribution">
            {isAR
              ? `المصدر: دائرة الأراضي والأملاك في دبي (DLD)، سجلّات الصفقات الرسمية. تُحدَّث أسبوعياً كل يوم اثنين، بحسب توفّر النشر.${source?.retrieved ? ` آخر تحديث: ${fmtIsoDate(source.retrieved, true)}.` : ""}`
              : `Source: Dubai Land Department (DLD), official transaction records. Updated weekly on Monday, subject to publication availability.${source?.retrieved ? ` Last refreshed: ${source.retrieved}.` : ""}`}
          </p>

          {/* methodology (how-to-read + data dictionary) lives in the "How to read the data" section further down */}
          <MonthlyBriefing isAR={isAR} />

          {/* Weekly interpretation leads, then the latest-week card */}
          {weeks[0] && summary && (
            <WeeklyInterpretation
              week={weeks[0]}
              priorWeeks={weeks.slice(1)}
              summary={summary}
              variant="compact"
            />
          )}
          {weeks[0] && <LatestWeekCard w={weeks[0]} isAR={isAR} lh={lh} format={format} formatBn={formatBn} />}

          {/* Summary panel — labels made explicit (procedures vs sales) after
              external audit. Order: (1) sales value, (2) sales count, (3) all
              procedures value, (4) all procedures count, then a second row for
              share + AED/sqft. Every card carries a compact provenance chip. */}
          {summary && (
            <>
              {/* Unified 6-tile KPI grid.
                  Mobile (grid-cols-2, 3 rows):
                    row 1: Sales value        · Sales count
                    row 2: All proc value     · All proc count
                    row 3: Off-plan share     · Median AED/sqft
                  Desktop (grid-cols-3, 2 rows):
                    row 1: Sales value · Sales count · Off-plan share
                    row 2: All proc value · All proc count · Median AED/sqft
                  DOM order = mobile order (natural flow). Two tiles carry
                  `md:order-*` classes so grid layout re-shuffles Off-plan
                  Share into desktop-slot 3 without needing duplicate JSX. */}
              {(() => { const tiles = [
                  {
                    k: isAR ? "قيمة المبيعات · حتى اليوم" : "Sales value · YTD 2026",
                    v: formatBn(summary.sales_aed_bn ?? summary.ytd?.sales_aed_bn),
                    m: isAR ? "بيع فعلي فقط" : "Pure sales only",
                    testid: "kpi-sales-value",
                    order: "",
                  },
                  {
                    k: isAR ? "عدد المبيعات · حتى اليوم" : "Sales count · YTD 2026",
                    v: fmtLocaleNum(summary.sales_count ?? summary.ytd?.sales_count, isAR),
                    m: isAR ? "بيع فعلي فقط" : "Pure sales only",
                    testid: "kpi-sales-count",
                    order: "",
                  },
                  {
                    k: isAR ? "قيمة كل الإجراءات · حتى اليوم" : "All procedures value · YTD 2026",
                    v: formatBn(summary.total_aed_bn),
                    m: isAR ? "بيع + رهون + هبات + نقل" : "Sales + mortgages + gifts + transfers",
                    testid: "kpi-procedures-value",
                    order: "md:order-4",
                  },
                  {
                    k: isAR ? "عدد كل الإجراءات · حتى اليوم" : "All procedures count · YTD 2026",
                    v: fmtLocaleNum(summary.total_transactions, isAR),
                    m: isAR ? "بيع + رهون + هبات + نقل" : "Sales + mortgages + gifts + transfers",
                    testid: "kpi-procedures-count",
                    order: "md:order-5",
                  },
                  {
                    k: isAR ? "حصة قيد الإنشاء (من المبيعات)" : "Off-plan share (of sales)",
                    v: summary.offplan_share_pct != null ? (isAR ? `${arDigits(summary.offplan_share_pct)}٪` : `${summary.offplan_share_pct}%`) : "—",
                    m: isAR ? "= مبيعات قيد الإنشاء ÷ إجمالي مبيعات دائرة الأراضي" : "= off-plan sales transactions ÷ total sales transactions",
                    testid: "kpi-offplan-share",
                    order: "md:order-3",
                  },
                  {
                    k: isAR ? "الوسيط لسعر القدم² (بيع)" : "Median AED / sqft (sales)",
                    v: summary.median_aed_per_sqft ? format(summary.median_aed_per_sqft) : "—",
                    m: isAR ? "الوسيط الرسمي (dld) · مبيعات فقط" : "DLD median · sales only",
                    testid: "kpi-median-sqft",
                    order: "",
                  },
                ];
                const tile = (s) => (
                  <div key={s.testid} className="rounded-2xl border hairline bg-white/70 p-4 sm:p-5" data-testid={s.testid}>
                    <p className="font-mono-brand text-[9px] sm:text-[10px] tracking-[0.16em] uppercase text-propertic-muted mb-2 leading-snug min-h-[2.4em]">{s.k}</p>
                    <p className="font-editorial text-2xl sm:text-3xl tabular-nums">{s.v}</p>
                    <p className="mt-2 font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-gold/90">
                      {s.m}
                    </p>
                  </div>
                );
                const isProc = (s) => s.testid.startsWith("kpi-procedures");
                return (
                  <section className="mt-10 sm:mt-14" data-testid="insights-key-indicators">
                    <h2 className="font-editorial text-2xl sm:text-3xl mb-4">{isAR ? "المؤشرات الرئيسية للسوق" : "Key market indicators"}</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="insights-summary">
                      {tiles.filter((s) => !isProc(s)).map(tile)}
                    </div>
                    <details className="mt-4 group" data-testid="insights-all-indicators">
                      <summary className="cursor-pointer list-none inline-flex items-center gap-2 text-xs font-mono-brand tracking-[0.14em] uppercase text-propertic-navy hover:text-propertic-gold" data-testid="insights-all-indicators-toggle">
                        {isAR ? "عرض كل الإجراءات (بيع + رهون + هبات + نقل)" : "Show all procedures (sales + mortgages + gifts + transfers)"}
                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                      </summary>
                      <div className="mt-4 grid grid-cols-2 gap-4 sm:gap-6 max-w-2xl">
                        {tiles.filter(isProc).map(tile)}
                      </div>
                    </details>
                  </section>
                );
              })()}
            </>
          )}

          {/* Useful navigation: three practical questions */}
          <section className="mt-12 sm:mt-16" data-testid="insights-useful-nav">
            <h2 className="font-editorial text-2xl sm:text-3xl mb-5">{isAR ? "ماذا تودّ أن تفهم؟" : "What would you like to understand?"}</h2>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
              {[
                { id: "market", to: "/dubai-market", t: isAR ? "ما مدى نشاط السوق؟" : "How active is the market?", b: isAR ? "استكشف تفاصيل المبيعات والإجراءات الكاملة مع المقارنات التاريخية." : "Explore the full sales and procedures breakdown with historical comparisons.", c: isAR ? "افتح السوق" : "Open Market" },
                { id: "compare", to: "/dubai-compare-areas", t: isAR ? "أي منطقة تناسب ميزانيتي؟" : "Which area fits my budget?", b: isAR ? "انتقل من نشاط دبي العام إلى مقارنة المناطق بشكل فردي." : "Go from Dubai-wide activity to comparing individual areas.", c: isAR ? "قارن المناطق" : "Compare Areas" },
                { id: "analyzer", to: "/property-deal-analyzer", t: isAR ? "ماذا يعني هذا بالنسبة لي؟" : "What does this mean for me?", b: isAR ? "استخدم روني أو محلّل الصفقات لربط سياق السوق الرسمي بقرار عقاري." : "Use Roni or the Deal Analyzer to connect official market context with a property decision.", c: isAR ? "حلّل صفقة" : "Analyze a deal", extra: { to: "/aiagent", c: isAR ? "أو اسأل روني" : "or ask Roni" } },
              ].map((n) => (
                <div key={n.id} className="rounded-2xl border hairline bg-white/70 p-5 sm:p-6 hover:border-propertic-gold transition-colors" data-testid={`insights-nav-${n.id}`}>
                  <h3 className="font-editorial text-lg mb-2">{n.t}</h3>
                  <p className="text-[13px] text-propertic-ink-2/85 leading-relaxed mb-4">{n.b}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] font-mono-brand tracking-[0.12em] uppercase">
                    <Link to={lh(n.to)} className="inline-flex items-center gap-1.5 text-propertic-navy hover:text-propertic-gold" data-testid={`insights-nav-${n.id}-link`}>{n.c}<ArrowUpRight className="w-3.5 h-3.5" /></Link>
                    {n.extra && <Link to={lh(n.extra.to)} className="text-propertic-gold hover:underline" data-testid={`insights-nav-${n.id}-roni`}>{n.extra.c}</Link>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* All weeks grid, grouped by month for SEO + readability */}
          <div className="mt-12 sm:mt-16">
            <h2 className="font-editorial text-2xl sm:text-3xl mb-2 flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-propertic-gold" />
              {isAR ? "كل الأسابيع" : "All weekly recaps"}
            </h2>
            <p className="text-[13px] text-propertic-muted mb-6">
              {isAR
                ? `${arDigits(weeks.length)} أسبوعاً منشوراً من دائرة الأراضي, من ${fmtIsoWeekLabel("2026-W01", true)} حتى ${fmtIsoWeekLabel(weeks[0]?.iso_week || "", true)}. كل أسبوع صفحة مستقلة.`
                : `${weeks.length} DLD-published weeks, from 2026-W01 to ${weeks[0]?.iso_week || ""}. Each week is a standalone page.`}
            </p>
            {loading && <p className="text-sm text-propertic-muted">{isAR ? "جاري التحميل…" : "Loading…"}</p>}
            {!loading && weeks.length === 0 && (
              <p className="text-sm text-propertic-muted italic">{isAR ? "لا توجد بيانات بعد." : "No weekly data yet."}</p>
            )}
            {weeks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="insights-recent-weeks">
                {weeks.slice(0, 4).map((w) => (
                  <Link key={w.iso_week} to={lh(`/insights/weekly/${w.iso_week}`)} className="rounded-2xl border hairline bg-white/70 p-4 hover:border-propertic-gold transition-colors" data-testid={`insights-recent-${w.iso_week}`}>
                    <p className="font-mono-brand text-[10px] tracking-[0.14em] text-propertic-muted">{fmtIsoDate(w.start_date, isAR)} → {fmtIsoDate(w.end_date, isAR)}</p>
                    <p className="font-editorial text-lg mt-1">{fmtIsoWeekLabel(w.iso_week, isAR)}</p>
                    <p className="text-[12px] text-propertic-ink-2/85 mt-1 tabular-nums">{fmtLocaleNum(w.tx_count, isAR)} {isAR ? "إجراء" : "procedures"} · {weekValueBn(w) == null ? "—" : formatBn(weekValueBn(w))}</p>
                  </Link>
                ))}
              </div>
            )}
            <details className="group" data-testid="insights-full-archive">
              <summary className="cursor-pointer list-none inline-flex items-center gap-2 h-10 px-5 mb-6 rounded-full border border-propertic-navy/30 hover:border-propertic-gold text-xs font-mono-brand tracking-[0.14em] uppercase" data-testid="insights-full-archive-toggle">
                {isAR ? "عرض الأرشيف الكامل" : "Show full archive"}
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
              </summary>
            {(() => {
              const monthName = (ym) => {
                const [y, m] = ym.split("-");
                const namesEN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                const namesAR = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
                const idx = parseInt(m, 10) - 1;
                return isAR ? `${namesAR[idx]} ${arDigits(y)}` : `${namesEN[idx]} ${y}`;
              };
              const byMonth = new Map();
              weeks.forEach((w) => {
                const ym = (w.start_date || "").slice(0, 7);
                if (!ym) return;
                if (!byMonth.has(ym)) byMonth.set(ym, []);
                byMonth.get(ym).push(w);
              });
              const months = [...byMonth.keys()].sort().reverse();
              return months.map((ym) => (
                <section key={ym} className="mb-10" data-testid={`insights-month-${ym}`}>
                  <h3 className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-3">
                    {monthName(ym)} · {isAR ? `${arDigits(byMonth.get(ym).length)} أسابيع` : `${byMonth.get(ym).length} weeks`}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {byMonth.get(ym).map((w) => {
                      // W22 (May 25-31, 2026) falls inside the Eid al-Adha
                      // public holidays (26-30 May 2026). DLD registrations
                      // drop sharply during Eid and total_aed_bn wasn't
                      // published for that week. Annotate the card so the
                      // ~10x drop reads as expected holiday behaviour, not
                      // a data bug.
                      const isEidWeek = w.iso_week === "2026-W22";
                      return (
                      <Link
                        key={w.iso_week}
                        to={lh(`/insights/weekly/${w.iso_week}`)}
                        data-testid={`insights-week-card-${w.iso_week}`}
                        className="group rounded-2xl border hairline bg-white/70 p-5 hover:border-propertic-gold hover:shadow-lg transition-all"
                      >
                        <div className="flex items-baseline justify-between mb-1">
                          <p className="font-editorial text-lg">{fmtIsoWeekLabel(w.iso_week, isAR)}</p>
                          <ArrowUpRight className="w-4 h-4 text-propertic-muted group-hover:text-propertic-gold transition-colors" />
                        </div>
                        <p className="text-[11px] text-propertic-muted mb-3 font-mono-brand tracking-[0.1em]">
                          {fmtIsoDate(w.start_date, isAR)} → {fmtIsoDate(w.end_date, isAR)}
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-[13px]">
                          <div>
                            <p className="text-[9px] uppercase tracking-[0.12em] text-propertic-muted">{isAR ? "الصفقات" : "Tx"}</p>
                            <p className="tabular-nums font-semibold">{fmtLocaleNum(w.tx_count, isAR)}</p>
                          </div>
                          <div>
                            <p className="text-[9px] uppercase tracking-[0.12em] text-propertic-muted">{isAR ? "القيمة" : "Value"}</p>
                            <p className="tabular-nums font-semibold">{weekValueBn(w) == null ? "—" : formatBn(weekValueBn(w))}</p>
                          </div>
                        </div>
                        {isEidWeek && (
                          <p className="mt-3 pt-3 border-t hairline text-[10px] text-amber-700 leading-relaxed" data-testid="insights-eid-note">
                            {isAR
                              ? "نشاط منخفض · عطلة عيد الأضحى (٢٦ إلى ٣٠ مايو ٢٠٢٦). قيمة الصفقات لهذا الأسبوع لم تُنشر من دائرة الأراضي."
                              : "Reduced activity · Eid al-Adha public holidays (26 to 30 May 2026). Transaction value for this week not published by DLD."}
                          </p>
                        )}
                      </Link>
                    );
                    })}
                  </div>
                </section>
              ));
            })()}
            </details>
          </div>

          <Methodology isAR={isAR} lh={lh} />

          {/* Source footer */}
          {source && (
            <div className="mt-14 pt-6 border-t hairline text-[11px] text-propertic-muted font-mono-brand" data-testid="insights-source-footer">
              {isAR ? "المصدر: " : "Source: "}
              <a href={source.url} target="_blank" rel="noreferrer" className="text-propertic-gold underline">
                {isAR ? source.name_ar : source.name}
              </a>
              {" · "}
              {isAR
                ? `آخر تحديث ناجح للبيانات: ${fmtIsoDate(source.retrieved, true)}`
                : `Dataset last refreshed ${source.retrieved}`}
              {" · "}
              {source.license}
            </div>
          )}
        </div>
      </div>

      {/* V2.0 P1 — Insights FAQ (10 Q&A). Uses the shared FAQ component with
          `variant="insights"` so the FAQPage JSON-LD is generated automatically
          for GEO engines. Content-only, zero new routes. */}
      <FAQ variant="insights" />
    </div>
  );
}
